import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box as Wrapper, Flex, Divider, Spinner, Text } from '@tonic-ui/react';
import { useCustomStyles } from '../../styles';
import { Alerts, useAlert } from '../../components/alert';
import ResourcesLinks from './resources-links';
import SupportAuthenticatedForm from './support-authenticated-form';
import { getC1Token } from '../../store';
import { useSendSupportRequest } from '../../services/support-api';
import SuccessPage from './success-page';
import ErrorPage from './error-page';

const SupportPage = (): JSX.Element => {
  const { divider } = useCustomStyles('support');
  const { t } = useTranslation();
  const { showError, closeAlerts } = useAlert();
  // For Error/Success screens
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
  const [customerContacts, setCustomerContacts] = useState<string>('');

  const { c1Token } = useSelector(
    (state) => ({
      c1Token: getC1Token(state, 'token') as string,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!c1Token) {
      showError(t('support.supportPage.noTokenError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c1Token]);

  const {
    mutateAsync: sendSupportRequest,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useSendSupportRequest(c1Token);

  if (isLoading) {
    return (
      <Flex
        data-id="waiting"
        direction="row"
        align="center"
        justify="center"
        with="100%"
        height="100%"
      >
        <Text size="3xl" fontWeight="semibold" mr="2x">
          {t('support.supportPage.submissionWaiting')}
        </Text>
        <Spinner />
      </Flex>
    );
  }

  if (isSuccess) {
    return (
      <SuccessPage
        contactMethod={contactMethod}
        customerContacts={customerContacts}
        id="support-form-success-page"
      />
    );
  }

  if (isError) {
    return <ErrorPage errorCode={error.status} />;
  }

  const handleSubmit = async ({ phoneNumber = '', phoneNumberExt = '', ...supportRequestData }) => {
    closeAlerts();
    const phone = phoneNumberExt ? `${phoneNumber} x${phoneNumberExt}` : `${phoneNumber}`;
    // TODO: Replace workloadSecurity with the correct service Id when more security services are in GovCloud.
    const shouldAddServiceId = ['threat', 'technical'].includes(supportRequestData?.issueType);
    await sendSupportRequest({
      ...supportRequestData,
      phone,
      service: shouldAddServiceId ? 'workloadSecurity' : '',
    } as SupportRequestData);
    // contacts for success state.
    setContactMethod(supportRequestData.contactMethod);
    setCustomerContacts(
      supportRequestData.contactMethod === 'email' ? supportRequestData.emails.join(', ') : phone
    );
  };

  return (
    <Flex direction="row" minHeight="100%">
      <Flex flex="1" direction="column">
        <Wrapper mt="16px">
          <Alerts />
        </Wrapper>
        <SupportAuthenticatedForm onSubmit={handleSubmit} />
      </Flex>
      <Divider orientation="vertical" borderColor={divider} width="2px" />
      <Flex height="100%" maxWidth="340px">
        <ResourcesLinks />
      </Flex>
    </Flex>
  );
};

SupportPage.displayName = 'SupportPage';
export default SupportPage;
